import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import { orgServiceApiSlice } from "state/services/orgServiceApiSlice";

// True while any Copilot dashboard query is in flight (scoped or enterprise).
const selectIsCopilotFetching = (state) =>
  Object.values(state.orgServiceApi?.queries ?? {}).some(
    (query) =>
      query?.status === "pending" &&
      (query.endpointName?.startsWith("getCopilot") || query.endpointName?.startsWith("getEnterpriseCopilot"))
  );

// How often the dashboard silently refreshes itself while it's open.
const AUTO_REFRESH_MS = 5 * 60 * 1000;

// Manual refresh for the Copilot dashboards. The read endpoints are cached
// (see copilotQueries.js), so this invalidates the shared "CopilotDashboard"
// tag: every query currently on screen refetches immediately, and cached
// entries for things not on screen (e.g. a closed modal) are dropped.
const CopilotRefreshButton = () => {
  const dispatch = useDispatch();
  const isFetching = useSelector(selectIsCopilotFetching);
  const lastRefreshRef = useRef(Date.now());

  const refresh = () => {
    lastRefreshRef.current = Date.now();
    dispatch(orgServiceApiSlice.util.invalidateTags(["CopilotDashboard"]));
  };

  // Timed silent refresh while the dashboard stays open (this button is
  // rendered on both Copilot dashboards, so it owns the timer). Skipped while
  // the tab is hidden; when the tab becomes visible again after being away
  // longer than the interval, it catches up straight away.
  useEffect(() => {
    const refreshIfStale = () => {
      if (document.visibilityState === "hidden") return;
      if (Date.now() - lastRefreshRef.current >= AUTO_REFRESH_MS) refresh();
    };
    const timer = setInterval(refreshIfStale, 30 * 1000);
    document.addEventListener("visibilitychange", refreshIfStale);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshIfStale);
    };
  }, []);

  return (
    <Tooltip title={isFetching ? "Refreshing…" : "Refresh all data"} placement="top" arrow>
      <span>
        <IconButton
          size="small"
          aria-label="Refresh dashboard data"
          disabled={isFetching}
          onClick={refresh}
          sx={{ color: "#555", border: "1px solid #ccc", width: 30, height: 30, "&:hover": { borderColor: "#26890d", color: "#26890d" } }}
        >
          {isFetching ? <CircularProgress size={16} sx={{ color: "#26890d" }} /> : <RefreshOutlinedIcon fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default CopilotRefreshButton;
